import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import useCurrentUser from 'hooks/useCurrentUser';
import type React from 'react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const SIDEBAR_WIDTH_EXPANDED = 250;
export const SIDEBAR_WIDTH_COLLAPSED = 72;

const SELECTED_BUSINESS_UNIT_STORAGE_KEY = 'saas_front_selectedBusinessUnitId';

const readPersistedBusinessUnitId = (): number | null => {
  try {
    const raw = localStorage.getItem(SELECTED_BUSINESS_UNIT_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
};

interface SidebarContextType {
  collapsed: boolean;
  toggleCollapsed: () => void;
  drawerWidth: number;
  selectedBusinessUnitId: number | null;
  setSelectedBusinessUnitId: (id: number | null) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedBusinessUnitId, setSelectedBusinessUnitIdState] = useState<number | null>(readPersistedBusinessUnitId);
  const { businessUnits } = useBusinessUnits();
  const currentUser = useCurrentUser();

  const setSelectedBusinessUnitId = useCallback((id: number | null) => {
    setSelectedBusinessUnitIdState(id);
    try {
      if (id === null) {
        localStorage.removeItem(SELECTED_BUSINESS_UNIT_STORAGE_KEY);
      } else {
        localStorage.setItem(SELECTED_BUSINESS_UNIT_STORAGE_KEY, String(id));
      }
    } catch {
      // Ignore storage errors (e.g., private mode, quota exceeded)
    }
  }, []);

  // Non-admins: pick first allowed BU when none is selected, or when the persisted
  // selection is no longer in the user's business units (stale localStorage / lost access).
  useEffect(() => {
    if (currentUser?.isAdmin || !businessUnits || businessUnits.length === 0) {
      return;
    }
    const hasAccess =
      selectedBusinessUnitId !== null &&
      selectedBusinessUnitId !== -1 &&
      businessUnits.some((businessUnit) => businessUnit.id === selectedBusinessUnitId);
    if (!hasAccess) {
      setSelectedBusinessUnitId(businessUnits[0].id);
    }
  }, [businessUnits, currentUser?.isAdmin, selectedBusinessUnitId, setSelectedBusinessUnitId]);

  const value = useMemo(
    () => ({
      collapsed,
      toggleCollapsed: () => setCollapsed((prev) => !prev),
      drawerWidth: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
      selectedBusinessUnitId,
      setSelectedBusinessUnitId,
    }),
    [collapsed, selectedBusinessUnitId, setSelectedBusinessUnitId]
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

export function useSelectedBusinessUnit() {
  const { selectedBusinessUnitId, setSelectedBusinessUnitId } = useSidebar();
  return { selectedBusinessUnitId, setSelectedBusinessUnitId };
}
